<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu/>

          <div class="element-box mt-5">
            <div class="row">
              <!-- 1. Einzelcoaching -->
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>

              <!-- 2. DRV Fallmanagement -->
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  icon="fa-briefcase"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>

              <!-- 3. Gruppenmaßnahmen -->
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  icon="fa-users"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>

              <!-- 4. Private Weiterbildung -->
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  icon="fa-graduation-cap"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <div class="row g-4 mt-4">
            <!-- 1. Standorte -->
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CoachingCardInfo
                  label="Standorte"
                  type="locations"
                  :value="infoData?.countLocations"
              />
            </div>

            <!-- 2. Aktive Kunden -->
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CoachingCardInfo
                  label="Aktive Kunden"
                  type="activeCustomers"
                  :value="infoData?.coachingTotals?.activeCoaching"
              />
            </div>

            <!-- 3. Neue Kunden -->
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CoachingCardInfo
                  label="Neue Kunden"
                  type="newCustomers"
                  :value="infoData?.coachingTotals?.newCoaching"
              />
            </div>

            <!-- 4. Abgeschlossene Kunden -->
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CoachingCardInfo
                  label="Abgeschlossene Kunden"
                  type="finishedCustomers"
                  :value="infoData?.coachingTotals?.finishedCoaching"
              />
            </div>
          </div>

          <div class="element-box mt-4">
            <div class="row">
              <div class="col-lg-6 col-md-6 col-sm-12">
                <h6 class="mt-3">Standortinformationen</h6>
              </div>
              <div class="col-lg-6 col-md-6 col-sm-12 text-end">
                <ExcelExportButton
                    label="Standortinformationen Excelexport"
                    @click="downloadFile('/dashboard/practice-manager/location-data-excel-export',
                    undefined, '', false)"
                />
              </div>
            </div>

            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="table-responsive">
                  <table class="table table-striped">
                    <thead>
                    <tr>
                      <th>Standort</th>
                      <th> Bundesland</th>
                      <th class="text-end">Aktive Kunden</th>
                      <th class="text-end">Neu Kunden</th>
                      <th class="text-end">Abgeschlossen Kunden</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(locationData) in infoData.dataByLocations" :key="locationData.id">
                      <td>{{ locationData?.locationName }}</td>
                      <td>{{ locationData?.federalState }}</td>
                      <td class="text-end">{{ locationData?.countActiveCoaching }}</td>
                      <td class="text-end">{{ locationData?.countNewCoaching }}</td>
                      <td class="text-end">{{ locationData?.countFinishedCoaching }}</td>
                    </tr>
                    <tr>
                      <td style="font-weight: bolder">gesamt</td>
                      <td style="font-weight: bolder" class="text-end"></td>
                      <td style="font-weight: bolder" class="text-end">{{
                          infoData?.coachingTotals?.activeCoaching
                        }}
                      </td>
                      <td style="font-weight: bolder" class="text-end">{{
                          infoData?.coachingTotals?.newCoaching
                        }}
                      </td>
                      <td style="font-weight: bolder" class="text-end">{{
                          infoData?.coachingTotals?.finishedCoaching
                        }}
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
                <Pagination
                    v-if="infoData?.pagination?.total_pages ?? 0 > 1"
                    :totalPages="infoData?.pagination?.total_pages"
                    :currentPage="infoData?.pagination?.current_page"
                    @pagechanged="getInfoData"/>
              </div>
            </div>
          </div>

        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box pie mt-2">
          <h6>Kunden info</h6>
          <VueApexCharts
              :key="seriesCoachingData.length"
              width="100%"
              type="donut"
              :options="chartOptions"
              :series="seriesCoachingData"/>
        </div>
        <div class="element-box">
          <h6>Standorte nach Bundesländern</h6>
          <div v-for="user of infoData?.countOfficeByFederalState" v-bind:key="user?.name"
               class="d-flex justify-content-between">
            <span>{{ user?.federalState }}</span>
            <span>{{ user?.count }}</span>
          </div>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardPracticeManagerService from '@/api/Dashboard/PracticeManager';
import CoachingButton from '@/components/button/CoachingButton.vue';
import CoachingCardInfo from '@/components/card/CoachingCardInfo.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
import {downloadFile} from '@/shared/functions';
import {getDonutColorsByCompany, optionsChartDonut} from '@/shared/utils/apex';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/PracticeManager/Menu.vue';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import VueApexCharts from 'vue3-apexcharts';
import Pagination from "@/components/Pagination.vue";
import ExcelExportButton from "@/components/ExcelExportButton.vue";

const {t} = useI18n();

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions = ref(optionsChartDonut);
const seriesCoachingData = ref([]);

const infoData = ref({});

async function getInfoData(page = 1, perPage = 15) {
  try {

    const perPageRes = infoData.value?.pagination?.items_per_page;

    const {data} = await dashboardPracticeManagerService.getInfoData({
      page: page,
      limit: perPage ?? perPageRes,
    });

    infoData.value = data.data;

    const coachingTotals = data.data.coachingTotals ?? {};
    const series = [
      coachingTotals.activeCoaching ?? 0,
      coachingTotals.newCoaching ?? 0,
      coachingTotals.finishedCoaching ?? 0,
    ];
    const totals = series.reduce((sum, value) => sum + value, 0);

    seriesCoachingData.value = series;
    chartOptions.value = {
      ...chartOptions.value,
      colors: getDonutColorsByCompany(companySettings.value?.companyKey),
      responsive: [{
        breakpoint: 2480,
        options: {
          chart: {
            width: '100%',
            height: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      }],
      labels: [
        `Aktive Kunden ${series[0]}`,
        `Neue Kunden ${series[1]}`,
        `Abgeschlossene Kunden ${series[2]}`,
      ],

      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'left',
        fontSize: '11px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        textColor: '#64748b',
        itemMargin: {
          horizontal: 6,
          vertical: 2
        },
        markers: {
          width: 8,
          height: 8,
          radius: 12,
          offsetX: -3
        }
      },
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => totals.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

onMounted(() => {
  CloseToast();
  getInfoData();
});
</script>

<style scoped>

::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}

</style>